import React, { useState, useEffect } from 'react';
import AIStudioChat from "./AIStudio/AIStudioChat";

interface FrontendMappingDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onProceedWithMappings: (mappings: Record<string, string>) => void;
    availableActors: string[];
    userCanisters: any[];
    project: any;
    getCanisterNameFromActor: (actorFile: string) => string;
}

const FrontendMappingDialog: React.FC<FrontendMappingDialogProps> = ({
                                                                         isOpen,
                                                                         onClose,
                                                                         onProceedWithMappings,
                                                                         availableActors,
                                                                         userCanisters,
                                                                         project,
                                                                         getCanisterNameFromActor
                                                                     }) => {
    const [actorMappings, setActorMappings] = useState<Record<string, string>>({});

    const backendCanisters = userCanisters?.filter(c =>
        c.canisterType === 'backend' &&
        project.canisters.some((projectCanister) =>
            projectCanister.toText() === c.principal.toText()
        )
    ) || [];


    // Initialize mappings object when dialog opens
// 🔧 FIXED: Only initialize mappings once when dialog opens
    useEffect(() => {
        if (isOpen && availableActors.length > 0) {
            // Only initialize if we don't have any mappings yet
            const hasExistingMappings = Object.keys(actorMappings).length > 0;

            if (!hasExistingMappings) {
                const initialMappings: Record<string, string> = {};
                const projectName = project?.name || 'unknown-project';

                availableActors.forEach(actor => {
                    const canisterName = getCanisterNameFromActor(actor);
                    const storageKey = `${projectName}:${canisterName}`;

                    const storedMapping = localStorage.getItem(storageKey);
                    if (storedMapping && storedMapping.trim() !== '') {
                        const canisterExists = backendCanisters.some(c => c.principal.toText() === storedMapping);
                        if (canisterExists) {
                            initialMappings[canisterName] = storedMapping;
                            console.log(`✅ Pre-populated frontend mapping: ${canisterName} → ${storedMapping}`);
                        } else {
                            initialMappings[canisterName] = '';
                            console.log(`⚠️ Stored canister ${storedMapping} no longer exists for ${canisterName}`);
                        }
                    } else {
                        initialMappings[canisterName] = '';
                    }
                });

                setActorMappings(initialMappings);
            }
        }
    }, [isOpen]); // 🔧 ONLY depend on isOpen


    const updateMapping = (canisterName: string, canisterId: string) => {
        setActorMappings(prev => ({
            ...prev,
            [canisterName]: canisterId
        }));
    };

    const handleProceed = () => {
        // Validate all mappings are filled
        const validMappings = Object.entries(actorMappings).filter(([_, canisterId]) => canisterId.trim() !== '');

        if (validMappings.length !== availableActors.length) {
            alert('Please select a canister for each actor before proceeding.');
            return;
        }

        // Store mappings in localStorage
        const projectName = project?.name || 'unknown-project';
        Object.entries(actorMappings).forEach(([canisterName, canisterId]) => {
            localStorage.setItem(`${projectName}:${canisterName}`, canisterId);
            console.log(`💾 Frontend dialog stored: ${projectName}:${canisterName} = ${canisterId}`);
        });

        onProceedWithMappings(actorMappings);
        onClose();
    };

    const allMappingsComplete = Object.values(actorMappings).every(id => id.trim() !== '');

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
        }}>
            <div style={{
                backgroundColor: '#fdf6e3',
                borderRadius: '8px',
                padding: '24px',
                width: '600px',
                maxHeight: '80vh',
                overflowY: 'auto',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}>
                <h3 style={{
                    margin: '0 0 16px 0',
                    color: '#5C4033',
                    fontSize: '20px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    🔵 Frontend Deploy Setup
                </h3>

                <div style={{
                    padding: '16px',
                    backgroundColor: '#e3f2fd',
                    border: '1px solid #2196f3',
                    borderRadius: '6px',
                    marginBottom: '20px'
                }}>
                    <p style={{
                        margin: '0 0 12px 0',
                        color: '#1976d2',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}>
                        💡 Frontend deployment requires backend canister mappings
                    </p>
                    <p style={{
                        margin: 0,
                        color: '#1976d2',
                        fontSize: '13px',
                        lineHeight: '1.4'
                    }}>
                        To generate correct <code>useCanister</code> hooks, each backend actor needs to be mapped to a deployed canister. These mappings will be saved for future deployments.
                    </p>
                </div>

                <div style={{marginBottom: '20px'}}>
                    <h4 style={{
                        margin: '0 0 16px 0',
                        color: '#5C4033',
                        fontSize: '16px',
                        fontWeight: '600'
                    }}>
                        Backend Actor → Canister Mappings:
                    </h4>

                    {availableActors.map((actorFile, index) => {
                        const canisterName = getCanisterNameFromActor(actorFile);
                        const actorDisplayName = actorFile.split('/').pop() || actorFile;

                        return (
                            <div key={actorFile} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                marginBottom: '16px',
                                padding: '16px',
                                backgroundColor: '#f3e5d8',
                                borderRadius: '6px',
                                border: '1px solid #e5d8c8'
                            }}>
                                <span style={{
                                    minWidth: '24px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#5C4033'
                                }}>
                                    {index + 1}.
                                </span>

                                <div style={{flex: 1}}>
                                    <div style={{
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: '#5C4033',
                                        marginBottom: '4px'
                                    }}>
                                        🟣 {actorDisplayName}
                                    </div>
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#8B4513'
                                    }}>
                                        Hook: <code>use{canisterName.charAt(0).toUpperCase() + canisterName.slice(1)}Canister()</code>
                                    </div>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <span style={{
                                        fontSize: '14px',
                                        color: '#5C4033',
                                        fontWeight: '500'
                                    }}>
                                        →
                                    </span>

                                    <select
                                        value={actorMappings[canisterName] || ''}
                                        onChange={(e) => updateMapping(canisterName, e.target.value)}
                                        style={{
                                            minWidth: '200px',
                                            padding: '8px 12px',
                                            borderRadius: '4px',
                                            border: '1px solid #8B4513',
                                            backgroundColor: '#fff',
                                            fontSize: '13px'
                                        }}
                                    >
                                        <option value="">Select canister...</option>
                                        {backendCanisters.map(canister => (
                                            <option key={canister.principal.toText()} value={canister.principal.toText()}>
                                                {canister.name || canister.principal.toText().substring(0, 12) + '...'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        );
                    })}

                    {backendCanisters.length === 0 && (
                        <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffc107',
                            borderRadius: '6px',
                            color: '#856404'
                        }}>
                            ⚠️ No backend canisters found in this project. Please create backend canisters first.
                        </div>
                    )}
                </div>

                {allMappingsComplete && (
                    <div style={{
                        marginBottom: '20px',
                        padding: '12px',
                        backgroundColor: '#e8f5e9',
                        border: '1px solid #4caf50',
                        borderRadius: '6px'
                    }}>
                        <div style={{
                            fontSize: '13px',
                            color: '#2e7d32',
                            fontWeight: '600',
                            marginBottom: '8px'
                        }}>
                            ✅ Ready to Deploy:
                        </div>
                        <div style={{
                            fontSize: '12px',
                            color: '#2e7d32',
                            lineHeight: '1.4'
                        }}>
                            {Object.entries(actorMappings).map(([canisterName, canisterId]) => {
                                const canisterInfo = backendCanisters.find(c => c.principal.toText() === canisterId);
                                return (
                                    <div key={canisterName}>
                                        • <strong>{canisterName}</strong> → {canisterInfo?.name || canisterId.substring(0, 12) + '...'}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end'
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#8B4513',
                            color: '#FFF5EE',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '500'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleProceed}
                        disabled={!allMappingsComplete || backendCanisters.length === 0}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: allMappingsComplete && backendCanisters.length > 0 ? '#2196f3' : '#ccc',
                            color: '#FFF5EE',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: allMappingsComplete && backendCanisters.length > 0 ? 'pointer' : 'not-allowed',
                            fontWeight: '500'
                        }}
                    >
                        🔵 Save Mappings & Deploy Frontend
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FrontendMappingDialog;
